import { useMemo } from "react";

import { useSelector } from "react-redux";

import { translate } from "app/filters/i18nFilter";
import { capitalizeFirstLetterFilter } from "app/filters/ngFilters";
import type { CrmIntegrationProvider } from "app/pages/sales-intelligence/domain/crm-integration/types/provider";
import type { CrmField } from "app/pages/sales-intelligence/domain/leads/types/filter-options";
import {
    type ICrmFieldsFilterDto,
    selectCrmFieldLabelByName,
} from "app/pages/sales-intelligence/modules/leads-search-filters";
import type { IFieldLabels } from "app/pages/sales-intelligence/modules/leads-search-filters/types/options.dto";
import type { InclusionOption } from "app/pages/sales-intelligence/types/commonMaps";
import { is } from "app/pages/sales-intelligence/utils/type-guards";

interface IUseCrmFieldsFilterProps {
    integrationName: CrmIntegrationProvider;
    fields: readonly CrmField[];
    value?: ICrmFieldsFilterDto;
    onFilterUpdate: (update: ICrmFieldsFilterDto) => void;
    inclusionOption: InclusionOption;
    labelOptions?: {
        prefixKey?: string;
        selectKey?: string;
    };
}

export const useCrmFieldsFilter = ({
    integrationName,
    fields,
    value,
    onFilterUpdate,
    inclusionOption,
    labelOptions = {},
}: IUseCrmFieldsFilterProps) => {
    const { prefixKey, selectKey = "common.select" } = labelOptions;

    const getNewFieldSelection = (
        isAllSelected: boolean,
        selectedItems: readonly string[],
    ): readonly string[] | undefined => {
        if (isAllSelected && selectedItems.length) {
            return [];
        }
        if (selectedItems.length) {
            return selectedItems;
        }
        return undefined;
    };

    const updateFieldSelection = (fieldName: string, selectedItems: readonly string[], isAllSelected: boolean) => {
        const otherFields = value ? value.listFieldsFilters.filter((f) => f.fieldName !== fieldName) : [];

        const newFieldSelection = getNewFieldSelection(isAllSelected, selectedItems);
        const updatedFilters = newFieldSelection
            ? [...otherFields, { fieldName, fieldValues: newFieldSelection }]
            : otherFields;

        onFilterUpdate({
            inclusion: inclusionOption,
            listFieldsFilters: updatedFilters,
        });
    };

    const fieldLabels = useSelector((state) =>
        selectCrmFieldLabelByName(state, [integrationName, fields.map((f) => f.fieldName)]),
    );

    const createFieldLabels = useMemo(() => {
        const labels: Record<string, IFieldLabels> = {};

        for (const [key, val] of Object.entries(fieldLabels)) {
            const name = val.toLowerCase();
            labels[key] = {
                placeholder: `${translate(selectKey)} ${name}`,
                label: prefixKey ? `${translate(prefixKey)} ${name}` : capitalizeFirstLetterFilter()(name),
            };
        }

        return labels;
    }, [fieldLabels, prefixKey, selectKey]);

    const getSelectedValues = (fieldName: string): readonly string[] => {
        const values = value?.listFieldsFilters?.find((f) => f.fieldName === fieldName)?.fieldValues;

        if (is.undefined(values)) {
            return [];
        }

        if (!values.length) {
            return fields.find((c) => c.fieldName == fieldName)?.fieldValuesMap?.map((c) => c.key) ?? [];
        }

        return values;
    };

    return {
        updateFieldSelection,
        getSelectedValues,
        fieldLabels: createFieldLabels,
    };
};
